import React, { useState } from 'react'
import { LevelData } from '../data/levelData'
import LevelInfoCard from './LevelInfoCard'

interface LevelNodeProps {
  data: LevelData
  isCurrentLevel: boolean
}

const LevelNode: React.FC<LevelNodeProps> = ({ data, isCurrentLevel }) => {
  const [showInfoCard, setShowInfoCard] = useState(false)
  const [hideTimeout, setHideTimeout] = useState<number | null>(null)

  const handleMouseEnter = () => {
    // 清除任何待處理的隱藏 timeout
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }
    setShowInfoCard(true)
  }

  const handleMouseLeave = () => {
    // 添加延遲，讓用戶有時間移動到 info card 上
    const timeout = setTimeout(() => {
      setShowInfoCard(false)
      setHideTimeout(null)
    }, 150) as unknown as number
    setHideTimeout(timeout)
  }

  const handleInfoCardMouseEnter = () => {
    // 當滑鼠進入 info card 時，清除隱藏 timeout 並保持顯示
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }
    setShowInfoCard(true)
  }

  const handleInfoCardMouseLeave = () => {
    // 當滑鼠離開 info card 時，立即隱藏卡片
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }
    setShowInfoCard(false)
  }

  // 清理 effect
  React.useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
      }
    }
  }, [hideTimeout])

  // 取得等級徽章的樣式類別
  const getBadgeClasses = () => {
    let classes = `
      w-20 h-20 md:w-24 md:h-24 rounded-full 
      flex items-center justify-center 
      font-bold text-lg text-white 
      border-4 transition-all duration-300 
      shadow-lg hover:shadow-xl
      ${data.borderClass}
    `

    // 基礎背景
    if (data.colorClass.includes('gradient')) {
      classes += ` ${data.colorClass}`
    } else {
      classes += ` ${data.colorClass} bg-gradient-to-br ${data.gradientClass}`
    }

    // 當前等級特效
    if (isCurrentLevel) {
      classes += ` scale-110 z-10 animate-pulse`
      if (!data.isLegendary && !data.isPartner) {
        classes += ` shadow-primary-400/50`
      }
    }

    // 特殊等級陰影
    if (data.isSpecial && !isCurrentLevel) {
      classes += ` shadow-purple-400/30`
    }
    if (data.isLegendary && !isCurrentLevel) {
      classes += ` shadow-pink-400/30`
    }
    if (data.isPartner && !isCurrentLevel) {
      classes += ` shadow-yellow-400/40`
    }

    return classes
  }

  return (
    <div className={`relative flex flex-col items-center ${showInfoCard ? 'z-[100]' : ''}`}>
      {/* 等級節點容器 */}
      <div
        className={`
          relative cursor-pointer transition-transform duration-300 
          ${isCurrentLevel ? 'z-20' : 'hover:scale-105 hover:-translate-y-2'}
          ${showInfoCard ? 'z-[100]' : ''}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* 當前等級水波紋效果 */}
        {isCurrentLevel && (
          <>
            <div className="absolute inset-0 rounded-full border-2 border-primary-400 opacity-0 animate-ping animation-delay-0"></div>
            <div className="absolute inset-0 rounded-full border-2 border-primary-400 opacity-0 animate-ping animation-delay-1000"></div>
            <div className="absolute -top-6 -right-2 text-2xl animate-bounce">✨</div>
          </>
        )}

        {/* 等級徽章 */}
        <div className={getBadgeClasses()}>
          <span className="text-shadow">LV{data.level}</span>
        </div>


        {/* 資訊卡片 */}
        <LevelInfoCard 
          data={data}
          isVisible={showInfoCard}
          isCurrentLevel={isCurrentLevel}
          onMouseEnter={handleInfoCardMouseEnter}
          onMouseLeave={handleInfoCardMouseLeave}
        />
      </div>

      {/* 等級名稱 */}
      <div className="mt-3 text-center">
        <h3 className={`
          font-semibold text-sm md:text-base
          ${isCurrentLevel ? 'text-primary-700 font-bold' : 'text-gray-700'}
          ${isCurrentLevel ? 'animate-pulse' : ''}
        `}>
          {data.name}
        </h3>
        
        {/* 時薪資訊 */}
        <div className={`
          text-xs md:text-sm text-gray-600 mt-1
          ${isCurrentLevel ? 'text-primary-600 font-semibold' : ''}
        `}>
          時薪 NT$ {data.hourlyRate}
        </div>

        {/* 升等時間要求 */}
        <div className={`
          inline-block mt-2 px-3 py-1 rounded-full text-xs
          border transition-all duration-300
          ${isCurrentLevel 
            ? 'bg-primary-100 text-primary-700 border-primary-300 animate-pulse' 
            : 'bg-amber-50 text-amber-700 border-amber-200'
          }
        `}>
          ⏰ {data.timeRequirement}
        </div>

        {/* 特殊等級標記 */}
        {data.isLegendary && (
          <div className="mt-2 text-xs text-purple-600 font-semibold animate-pulse">
            ✨ 傳奇階段
          </div>
        )}
        
        {data.isPartner && (
          <div className="mt-2 text-xs text-yellow-600 font-semibold animate-pulse">
            👑 合夥人級別
          </div>
        )}
      </div>
    </div>
  )
}

export default LevelNode
